import { createContext, useContext, useState, useEffect } from 'react'

const ThemeContext = createContext()

// Constants
const THEME_STORAGE_KEY = 'theme'
const THEME_DARK = 'dark'
const THEME_LIGHT = 'light'

// Utility functions
const getSystemTheme = () => {
  try {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return THEME_DARK
    }
  } catch (error) {
    console.warn('Failed to detect system theme preference:', error)
  }
  return THEME_LIGHT
}

const getStoredTheme = () => {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY)
    if (stored === THEME_DARK || stored === THEME_LIGHT) {
      return stored
    }
  } catch (error) {
    console.warn('Failed to load theme preference from localStorage:', error)
  }
  return null
}

const getInitialTheme = () => {
  // Priority: stored preference > system preference > default (light)
  const stored = getStoredTheme()
  if (stored) {
    return stored
  }
  return getSystemTheme()
}

const saveTheme = (theme) => {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme)
  } catch (error) {
    console.warn('Failed to save theme preference to localStorage:', error)
    // Theme will still work, just won't persist
  }
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(getInitialTheme)

  // Apply theme class to document root
  useEffect(() => {
    const root = document.documentElement
    
    // Remove both classes first
    root.classList.remove(THEME_DARK, THEME_LIGHT)
    
    // Add the current theme class
    root.classList.add(theme)
  }, [theme])

  // Persist theme changes to localStorage
  useEffect(() => {
    saveTheme(theme)
  }, [theme])

  // Listen for system preference changes
  useEffect(() => {
    try {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      
      const handleChange = (e) => {
        // Only update if user hasn't set a preference
        const stored = getStoredTheme()
        if (!stored) {
          setThemeState(e.matches ? THEME_DARK : THEME_LIGHT)
        }
      }

      // Modern browsers
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleChange)
        return () => mediaQuery.removeEventListener('change', handleChange)
      }
      // Legacy browsers
      else if (mediaQuery.addListener) {
        mediaQuery.addListener(handleChange)
        return () => mediaQuery.removeListener(handleChange)
      }
    } catch (error) {
      console.warn('Failed to listen for system theme changes:', error)
    }
  }, [])

  const setTheme = (newTheme) => {
    if (newTheme === THEME_DARK || newTheme === THEME_LIGHT) {
      setThemeState(newTheme)
    } else {
      console.warn(`Invalid theme value: ${newTheme}. Must be '${THEME_DARK}' or '${THEME_LIGHT}'.`)
    }
  }

  const toggleTheme = () => {
    setThemeState(prevTheme => prevTheme === THEME_DARK ? THEME_LIGHT : THEME_DARK)
  }

  const value = {
    theme,
    setTheme,
    toggleTheme,
    isDark: theme === THEME_DARK,
    isLight: theme === THEME_LIGHT,
  }

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error(
      'useTheme must be used within a ThemeProvider. ' +
      'Wrap your component tree with <ThemeProvider>.'
    )
  }
  return context
}

export default ThemeContext
